import { FC } from "react";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";

import { CustomTypography } from "../../../components/CustomTypography/CustomTypography";
import { useGetVehiculesByEmployee } from "../../../api/vehicule";
import { getCurrentFleetId } from "../../../utils/getCurrentFleet";
import useStyles from "./styles";

type props = {
  /** user.id of the collaborator whose assignments to list. */
  userId: number;
};

/**
 * Vehicles currently assigned to a collaborator.
 *
 * Previously this rendered `Array.from({ length: 2 })` of placeholder strings
 * — literally "Du xx/xx/2022 au xx/xx/2023", "Immat", "Logo marque", "Modele",
 * "Cat" — for every collaborator, regardless of what they actually drive. Two
 * rows always, even for someone with no vehicle at all.
 *
 * The assignment endpoint already existed and was in use on the collaborator
 * sheet; it just was not wired up here.
 */
export const InfosCollabDashboard: FC<props> = ({ userId }) => {
  const classes = useStyles();
  const { t } = useTranslation();

  const { vehicules, isLoading } = useGetVehiculesByEmployee(
    getCurrentFleetId(),
    userId
  );

  const formatDate = (value?: string) =>
    value ? dayjs(value).format("DD/MM/YYYY") : "—";

  /** Whole months between the two bounds; blank when either is missing. */
  const durationLabel = (start?: string, end?: string) => {
    if (!start || !end) return null;
    const months = dayjs(end).diff(dayjs(start), "month");
    if (!Number.isFinite(months) || months <= 0) return null;
    return t("vehicule.durationMonths", {
      count: months,
      defaultValue: `Duration: ${months} months`,
    });
  };

  if (isLoading) {
    return null;
  }

  // Deliberately silent when there is nothing to list. ResultSearchCollab owns
  // the empty-state copy (it is the component that can tell "no vehicle" apart
  // from "no active contract"); rendering a second message here would print the
  // same sentence twice, one above the other.
  if (vehicules.length === 0) {
    return null;
  }

  return (
    <div className={classes.divInfosCollab}>
      {vehicules.map((vehicule: TVehicule) => {
        const duration = durationLabel(vehicule.start_date, vehicule.end_date);

        return (
          <div
            className={classes.divInfosCollabDashboard}
            key={vehicule.fleetVehicleAssignedId ?? vehicule.vehicleId}
          >
            <div className={classes.divDate}>
              <CustomTypography
                text={t("common.fromTo", {
                  from: formatDate(vehicule.start_date),
                  to: formatDate(vehicule.end_date),
                  defaultValue: `From ${formatDate(
                    vehicule.start_date
                  )} to ${formatDate(vehicule.end_date)}`,
                })}
                variant="link"
              />
              {duration && <CustomTypography text={duration} variant="link" />}
            </div>
            <div className={classes.divImat}>
              <CustomTypography
                text={vehicule.immatriculation || "—"}
                variant={"default"}
                isUppercase
              />
              <CustomTypography
                text={vehicule.brand || "—"}
                variant={"default"}
                isUppercase
              />
              <CustomTypography
                text={vehicule.brandModel || "—"}
                variant={"default"}
                isUppercase
              />
              <CustomTypography
                text={vehicule.carburant || "—"}
                variant={"default"}
                isUppercase
              />
            </div>
          </div>
        );
      })}
    </div>
  );
};
